import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { AGENTS } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Save, Trash2, Play } from "lucide-react";
import { AgentFormPanel } from "@/pages/agents/AgentFormPanel";
import { AgentPlayground } from "@/pages/agents/AgentPlayground";

/**
 * Agent editor orchestrator. Presentational blocks live in
 * `/pages/agents/AgentFormPanel.jsx` (form) and `AgentPlayground.jsx` (chat).
 */
export default function AgentEditor() {
  const params = useParams();
  const [search] = useSearchParams();
  const isNew = params.id === "new" || !params.id;
  const leadIdFromQuery = search.get("lead_id");
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "", role: "both", tone: "friendly and professional",
    business_info: "", services: [], faqs: [],
    business_hours: "", greeting: "", tagline: "",
    booking_enabled: true,
  });
  const [lead, setLead] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newService, setNewService] = useState("");
  const [newFaqQ, setNewFaqQ] = useState("");
  const [newFaqA, setNewFaqA] = useState("");

  useEffect(() => {
    (async () => {
      if (isNew && leadIdFromQuery) {
        const { data } = await api.get(`/leads/${leadIdFromQuery}`);
        setLead(data.lead);
        setForm((f) => ({
          ...f,
          name: `${data.lead.name.split(" ")[0]} Assistant`,
          tagline: `${data.lead.name} · Assistant`,
          greeting: `Hi! Thanks for stopping by ${data.lead.name}. How can we help?`,
          business_info: `${data.lead.name} is a ${data.lead.industry} in ${data.lead.city}, ${data.lead.state}. Rated ${data.lead.rating}★ with ${data.lead.review_count} reviews. Reach us at ${data.lead.phone}.`,
        }));
      } else if (!isNew) {
        try {
          const { data } = await api.get(`/agents/${params.id}`);
          setLead(data.lead);
          setAgentId(data.agent.id);
          setForm({
            name: data.agent.name || "",
            role: data.agent.role || "both",
            tone: data.agent.tone || "friendly and professional",
            business_info: data.agent.business_info || "",
            services: data.agent.services || [],
            faqs: data.agent.faqs || [],
            business_hours: data.agent.business_hours || "",
            greeting: data.agent.greeting || "",
            tagline: data.agent.tagline || "",
            booking_enabled: data.agent.booking_enabled !== false,
          });
        } catch (e) { toast.error(formatApiError(e)); }
      }
    })();
  }, [params.id, leadIdFromQuery, isNew]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addService = () => {
    if (!newService.trim()) return;
    update("services", [...form.services, newService.trim()]);
    setNewService("");
  };
  const removeService = (i) => update("services", form.services.filter((_, idx) => idx !== i));

  const addFaq = () => {
    if (!newFaqQ.trim() || !newFaqA.trim()) return;
    update("faqs", [...form.faqs, { q: newFaqQ.trim(), a: newFaqA.trim() }]);
    setNewFaqQ(""); setNewFaqA("");
  };
  const removeFaq = (i) => update("faqs", form.faqs.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        const { data } = await api.post("/agents", { lead_id: leadIdFromQuery, ...form });
        setAgentId(data.id);
        toast.success("Agent created");
        navigate(`/agents/${data.id}`, { replace: true });
      } else {
        await api.patch(`/agents/${params.id}`, form);
        toast.success("Agent updated");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this agent? Conversations will be kept.")) return;
    try {
      await api.delete(`/agents/${params.id}`);
      toast.success("Agent deleted");
      navigate("/agents");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid={AGENTS.editorContainer} className="p-10">
      <Toaster position="top-right" richColors />
      <Link to="/agents" className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-black mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to agents
      </Link>

      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="overline mb-2">
            {isNew ? "New agent" : "Editing agent"}
            {lead && <> · for {lead.name}</>}
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter">
            {isNew ? "Design your AI agent" : form.name}
          </h1>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <Link to={`/agents/${params.id}/demo`} data-testid="agent-editor-demo-link"
                  className="lf-btn-outline flex items-center gap-2 text-sm"
                  style={{ borderColor: "#FFB020", color: "#B45309" }}>
              <Play className="w-4 h-4" /> Demo mode
            </Link>
          )}
          {!isNew && (
            <button data-testid={AGENTS.deleteBtn} onClick={remove} className="lf-btn-outline flex items-center gap-2 text-sm">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
          <button data-testid={AGENTS.saveBtn} onClick={save} disabled={busy} className="lf-btn-primary flex items-center gap-2 text-sm">
            <Save className="w-4 h-4" />
            {busy ? "Saving…" : isNew ? "Create agent" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <AgentFormPanel
          form={form} update={update}
          newService={newService} setNewService={setNewService}
          addService={addService} removeService={removeService}
          newFaqQ={newFaqQ} setNewFaqQ={setNewFaqQ}
          newFaqA={newFaqA} setNewFaqA={setNewFaqA}
          addFaq={addFaq} removeFaq={removeFaq}
        />
        <AgentPlayground agentId={agentId} lead={lead} />
      </div>
    </div>
  );
}
